import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, UserCheck, UserX, Shield, Edit2, StopCircle, PlayCircle, Key, Loader2, Search, Filter, X, Building2, UserPlus, Upload } from 'lucide-react';
import Table from '../../Table/Table';
import CommonPagination from '../../Common/CommonPagination';
import Loading from '../../Common/Loading';
import UserModal from './UserModal';
import ChangePasswordModal from './ChangePasswordModal';
import ConfirmModal from '../../Common/ConfirmModal';
import { Tooltip } from '../../Common/Tooltip';
import Toast, { ToastType } from '../../Common/Toast';
import userService, { User, UserStats } from '../../../services/user.service';
import authService from '../../../services/auth.service';
import roleService from '../../../services/role.service';
import organisationService from '../../../services/organisation.service';
import styles from './UserManagement.module.css';
import ClientModal from '../../ClientManagement/ClientModal';
import clientService from '../../../services/client.service';
import ClientMappingModal from './ClientMappingModal';
import ClientSelectionModal from '../../SOP/SOPListing/ClientSelectionModal';
import { UserTypeModal } from './UserTypeModal';
import BulkUserUploadModal from './BulkUserUploadModal';
import { debounce } from '../../../utils/debounce';
import CommonMultiSelect from '../../Common/CommonMultiSelect';
import modulesService, { Module, Submodule } from '../../../services/modules.service';

// ─────────────────────────────────────────────────────────────────────────────
// ROLE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

type CallerRole =
    | 'SUPER_ADMIN'
    | 'SUPER_ADMIN_ORG'
    | 'ORGANISATION_ADMIN'
    | 'CLIENT_ADMIN'
    | 'USER';

function resolveCallerRole(user: any): CallerRole {
    if (!user) return 'USER';
    const roleName: string = user.role?.name ?? '';
    if (user.is_superuser || roleName === 'SUPER_ADMIN') {
        // If they have an organisation context they are scoped
        return user.organisation_id ? 'SUPER_ADMIN_ORG' : 'SUPER_ADMIN';
    }
    if (roleName === 'ORGANISATION_ADMIN') return 'ORGANISATION_ADMIN';
    if (roleName === 'CLIENT_ADMIN' || user.is_client) return 'CLIENT_ADMIN';
    return 'USER';
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const UserManagement: React.FC = () => {
    const currentUser = authService.getUser();
    const callerRole = resolveCallerRole(currentUser);

    const isSuperAdmin   = callerRole === 'SUPER_ADMIN' || callerRole === 'SUPER_ADMIN_ORG';
    const isOrgAdmin     = callerRole === 'ORGANISATION_ADMIN';
    const isClientAdmin  = callerRole === 'CLIENT_ADMIN';
    const isAdmin        = isSuperAdmin || isOrgAdmin || isClientAdmin; // can manage users
    
    /** Can see Internal Users / Client Users tabs */
    const canSeeTabs     = isSuperAdmin || isOrgAdmin;

    /** Can see the Filters sidebar */
    const canUseFilters  = isSuperAdmin || isOrgAdmin;

    /** Can see Org filter inside the sidebar (only superadmins span multiple orgs) */
    const canFilterOrg   = isSuperAdmin;

    /** Can see Client filter inside the sidebar */
    const canFilterClient = isSuperAdmin || isOrgAdmin;

    // ── Pagination ────────────────────────────────────────────────────────────
    const [currentPage, setCurrentPage]   = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // ── Data ─────────────────────────────────────────────────────────────────
    const [users, setUsers]           = useState<User[]>([]);
    const [stats, setStats]           = useState<UserStats | null>(null);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading]       = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // ── Tabs (only relevant when canSeeTabs) ──────────────────────────────────
    // null → show all; false → internal (client_id IS NULL); true → client (client_id IS NOT NULL)
    const [activeTab, setActiveTab] = useState<'internal' | 'client'>('internal');
    const activeTabIsClient: boolean | undefined = !canSeeTabs
        ? undefined                          // no tab filtering for CLIENT_ADMIN / USER
        : activeTab === 'client' ? true : false;

    // ── Modals ────────────────────────────────────────────────────────────────
    const [isModalOpen, setIsModalOpen]                 = useState(false);
    const [editingUser, setEditingUser]                 = useState<User | null>(null);
    const [changePasswordUser, setChangePasswordUser]   = useState<User | null>(null);
    const [isClientModalOpen, setIsClientModalOpen]     = useState(false);
    const [crossCreationData, setCrossCreationData]     = useState<any>(null);
    const [showCrossCreationConfirm, setShowCrossCreationConfirm] = useState(false);
    const [userTypeModalOpen, setUserTypeModalOpen]     = useState(false);
    const [clientSelectionOpen, setClientSelectionOpen] = useState(false);
    const [selectedClient, setSelectedClient]           = useState<any>(null);
    const [bulkUploadOpen, setBulkUploadOpen]           = useState(false);
    const [clientMappingModal, setClientMappingModal]   = useState<{ isOpen: boolean; user: User | null }>({ isOpen: false, user: null });
    const [confirmModal, setConfirmModal]               = useState<{ isOpen: boolean; user: User | null; action: 'toggle' }>({ isOpen: false, user: null, action: 'toggle' });
    const [toast, setToast]                             = useState<{ message: string; type: ToastType } | null>(null);
    const [loadingEditId, setLoadingEditId]             = useState<string | null>(null);

    // ── Roles ─────────────────────────────────────────────────────────────────
    const [roles, setRoles]           = useState<Array<{ id: string; name: string }>>([]);
    const [roleOptions, setRoleOptions] = useState<Array<{ label: string; value: string }>>([]);

    // ── Filters (applied) ─────────────────────────────────────────────────────
    const [statusFilter,         setStatusFilter]         = useState<string[]>([]);
    const [selectedRole,         setSelectedRole]         = useState<string[]>([]);
    const [selectedOrg,          setSelectedOrg]          = useState<string[]>([]);
    const [selectedClientFilter, setSelectedClientFilter] = useState<string[]>([]);
    const [createdByFilter,      setCreatedByFilter]      = useState<string[]>([]);

    // ── Filters (temp – inside offcanvas before Apply) ────────────────────────
    const [showFilters,             setShowFilters]             = useState(false);
    const [tempStatusFilter,        setTempStatusFilter]        = useState<string[]>([]);
    const [tempSelectedRole,        setTempSelectedRole]        = useState<string[]>([]);
    const [tempSelectedOrg,         setTempSelectedOrg]         = useState<string[]>([]);
    const [tempSelectedClientFilter,setTempSelectedClientFilter]= useState<string[]>([]);
    const [tempCreatedByFilter,     setTempCreatedByFilter]     = useState<string[]>([]);
    const [modules, setModules] = useState<Module[]>([]);
    const [submodules, setSubmodules] = useState<Submodule[]>([]);
    // ── Filter option lists ───────────────────────────────────────────────────
    const [organisations,      setOrganisations]      = useState<Array<{ label: string; value: string }>>([]);
    const [allClients,         setAllClients]          = useState<Array<{ label: string; value: string; organisationName?: string }>>([]);
    const [createdByOptions,   setCreatedByOptions]   = useState<Array<{ label: string; value: string; organisationName?: string }>>([]);

    // ── Search ────────────────────────────────────────────────────────────────
    const [searchQuery,    setSearchQuery]    = useState('');
    const [debouncedSearch,setDebouncedSearch]= useState('');

    // ── Refs ──────────────────────────────────────────────────────────────────
    const rolesLoadedRef    = useRef(false);
    const dataLoadingRef    = useRef(false);
    const lastCreatorFetchId = useRef(0);
    const lastRoleFetchId    = useRef(0);
    const [creatorSearch, setCreatorSearch] = useState('');
    const [roleSearch,    setRoleSearch]    = useState('');

    // ─────────────────────────────────────────────────────────────────────────
    // INITIALISE FIXED FILTERS based on role
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!currentUser) return;
        if (isOrgAdmin && currentUser.organisation_id) {
            setSelectedOrg([currentUser.organisation_id]);
            setTempSelectedOrg([currentUser.organisation_id]);
        }
        if (isClientAdmin && currentUser.client_id) {
    // DON'T auto filter immediately
    // let user see all first
}
        if (isClientAdmin) {
    if (currentUser.organisation_id) {
        setSelectedOrg([currentUser.organisation_id]);
        setTempSelectedOrg([currentUser.organisation_id]);
    }

    // ❗ DO NOT auto-apply client filter
    // only store it if needed later
}
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
const [permissionsLoaded, setPermissionsLoaded] = useState(false);

useEffect(() => {
    const loadPermissions = async () => {
        try {
            const user = authService.getUser();
            if (!user?.email) return;

            const data = await modulesService.getUserModules(user.email);
            setModules(data);
        } catch (error) {
            console.error('Failed to load permissions:', error);
        } finally {
            setPermissionsLoaded(true);
        }
    };

    loadPermissions();
}, []);
const userModule = modules
  .find(m => m.name === "users_permissions")
  ?.submodules?.find(sm => sm.name === "user_module");
const canViewUser =
    permissionsLoaded && (
        userModule?.privileges?.includes("READ") ?? false
    );
const canCreateUser =
  permissionsLoaded && (userModule?.privileges?.includes("CREATE") ?? false);
    const canEditUser =
    permissionsLoaded &&
  (userModule?.privileges?.includes("UPDATE") ?? false);

  
    // ─────────────────────────────────────────────────────────────────────────
    // DEBOUNCED SEARCH
    // ─────────────────────────────────────────────────────────────────────────
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedUpdate = useCallback(debounce((val: string) => {
        setDebouncedSearch(val);
        setCurrentPage(0);
    }, 500), []);

    const handleSearchChange = (val: string) => {
        setSearchQuery(val);
        debouncedUpdate(val);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // LOAD DATA
    // ─────────────────────────────────────────────────────────────────────────
    const loadData = async () => {
                 if (!permissionsLoaded || modules.length === 0) return;

    if (!canViewUser) {
        setIsInitialLoading(false); // 🔥 prevent infinite loader
        return;
    }

        if (dataLoadingRef.current) return;
        dataLoadingRef.current = true;
        try {
            setLoading(true);
            const [usersData, statsData] = await Promise.all([
                userService.getUsers(
                    currentPage + 1,
                    itemsPerPage,
                    debouncedSearch || undefined,
                    statusFilter.length > 0         ? statusFilter         : undefined,
                    selectedRole.length > 0         ? selectedRole         : undefined,
                    selectedOrg.length > 0          ? selectedOrg          : undefined,
                    selectedClientFilter.length > 0 ? selectedClientFilter : undefined,
                    createdByFilter.length > 0      ? createdByFilter      : undefined,
                    activeTabIsClient,
                ),
                userService.getUserStats(),
            ]);
            setUsers(usersData.users);
            setTotalUsers(usersData.total);
            setStats(statsData);
        } catch (error) {
            console.error('Failed to load users:', error);
            setToast({ message: 'Failed to load users', type: 'error' });
        } finally {
            setLoading(false);
            setIsInitialLoading(false);
            dataLoadingRef.current = false;
        }
    };

useEffect(() => {
if (!permissionsLoaded || modules.length === 0) return;
    loadData();
}, [
    permissionsLoaded,
    currentPage,
    itemsPerPage,
    statusFilter,
    debouncedSearch,
    selectedRole,
    selectedOrg,
    selectedClientFilter,
    createdByFilter,
    activeTab
]);

    // ─────────────────────────────────────────────────────────────────────────
    // LOAD ROLES & FILTER OPTIONS (admin roles only)
    // ─────────────────────────────────────────────────────────────────────────
   useEffect(() => {
    if (!rolesLoadedRef.current && currentUser) {
        rolesLoadedRef.current = true;
        loadRoles();
        loadFilterOptions();
    }
}, [currentUser]);// eslint-disable-line react-hooks/exhaustive-deps

    const loadRoles = async () => {
        try {
            const data = await roleService.getAssignableRoles(1, 100);
            setRoles(data.roles.map((r: any) => ({ id: r.id, name: r.name, organisation_id: r.organisation_id })));
        } catch (e) {
            console.error('Failed to load roles:', e);
            rolesLoadedRef.current = false;
        }
    };

    const loadFilterOptions = async () => {
        try {
            if (canFilterOrg) {
                const orgs = await organisationService.getOrganisations(1, 100);
                setOrganisations(orgs.organisations.map((o: any) => ({ label: o.name || o.username, value: o.id })));
            }
            const visibleClients = await clientService.getVisibleClients();
            const mapped = visibleClients.map((c: any) => ({
                label: c.business_name || c.first_name || 'Client',
                value: c.id,
                organisationName: c.organisation_name,
            }));
            setAllClients(mapped);
        } catch (e) {
            console.error('Failed to load filter options:', e);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // CREATOR & ROLE SEARCH (inside offcanvas)
    // ─────────────────────────────────────────────────────────────────────────
    const fetchCreators = async (searchVal: string, orgs: string[], clients: string[]) => {
        const requestId = Date.now();
        lastCreatorFetchId.current = requestId;
        try {
            const creators = await userService.getCreators(
                searchVal,
                orgs.length > 0 ? orgs : undefined,
                clients.length > 0 ? clients : undefined,
            );
            if (lastCreatorFetchId.current === requestId) {
                setCreatedByOptions(creators.map((u: any) => ({
                    label: `${u.first_name || ''} ${u.last_name || ''} (${u.username})`.trim(),
                    value: u.id,
                    organisationName: u.organisation_name,
                })));
            }
        } catch (e) { console.error('Failed to load creators', e); }
    };

    const fetchLightRoles = async (searchVal: string, orgs: string[], clients: string[]) => {
        const requestId = Date.now();
        lastRoleFetchId.current = requestId;
        try {
            const data = await roleService.getLightRoles(searchVal, orgs, clients);
            if (lastRoleFetchId.current === requestId) {
                setRoleOptions(data.map((r: any) => ({ label: r.name, value: r.id })));
            }
        } catch (e) { console.error('Failed to load light roles', e); }
    };

    // Fetch creators / roles when offcanvas opens or org/client temp filters change
    useEffect(() => {
        if (!showFilters || !canUseFilters) return;
        fetchCreators(creatorSearch, tempSelectedOrg, tempSelectedClientFilter);
        fetchLightRoles(roleSearch, tempSelectedOrg, tempSelectedClientFilter);
    }, [showFilters, tempSelectedOrg, tempSelectedClientFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce creator search input
    useEffect(() => {
        if (!showFilters) return;
        const h = setTimeout(() => fetchCreators(creatorSearch, tempSelectedOrg, tempSelectedClientFilter), 500);
        return () => clearTimeout(h);
    }, [creatorSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce role search input
    useEffect(() => {
        if (!showFilters) return;
        const h = setTimeout(() => fetchLightRoles(roleSearch, tempSelectedOrg, tempSelectedClientFilter), 500);
        return () => clearTimeout(h);
    }, [roleSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─────────────────────────────────────────────────────────────────────────
    // FILTER OFFCANVAS HANDLERS
    // ─────────────────────────────────────────────────────────────────────────
    const handleOpenFilters = () => {
        setTempStatusFilter(statusFilter);
        setTempSelectedRole(selectedRole);
        setTempSelectedOrg(selectedOrg);
        setTempSelectedClientFilter(selectedClientFilter);
        setTempCreatedByFilter(createdByFilter);
        setShowFilters(true);
    };

    const handleApplyFilters = () => {
        setStatusFilter(tempStatusFilter);
        setSelectedRole(tempSelectedRole);
        setSelectedOrg(tempSelectedOrg);
        setSelectedClientFilter(tempSelectedClientFilter);
        setCreatedByFilter(tempCreatedByFilter);
        setCurrentPage(0);
        setShowFilters(false);
    };

    const handleResetFilters = () => {
        setTempStatusFilter([]);
        setTempSelectedRole([]);
        // Keep org/client pinned for org/client admins
        if (!isOrgAdmin) setTempSelectedOrg([]);
        if (!isClientAdmin) setTempSelectedClientFilter([]);
        setTempCreatedByFilter([]);
    };

    const activeFilterCount = (() => {
        let c = 0;
        if (selectedRole.length > 0) c++;
        if (canFilterOrg && selectedOrg.length > 0) c++;
        if (canFilterClient && selectedClientFilter.length > 0) c++;
        if (statusFilter.length > 0) c++;
        if (createdByFilter.length > 0) c++;
        return c;
    })();

    // ─────────────────────────────────────────────────────────────────────────
    // STAT CARD CLICK
    // ─────────────────────────────────────────────────────────────────────────
    const handleStatClick = (type: 'total' | 'active' | 'inactive') => {
        if (type === 'total')    setStatusFilter([]);
        if (type === 'active')   setStatusFilter(['ACTIVE']);
        if (type === 'inactive') setStatusFilter(['INACTIVE']);
        setCurrentPage(0);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // USER ACTIONS
    // ─────────────────────────────────────────────────────────────────────────
    const handleEdit = async (user: User) => {
        try {
            setLoadingEditId(user.id);
            const fullUser = await userService.getUser(user.id);
            setEditingUser(fullUser);
            setIsModalOpen(true);
        } catch (e) { console.error(e); }
        finally { setLoadingEditId(null); }
    };

    const handleChangePassword = (user: User) => {
        if (user.is_superuser) {
            setToast({ message: 'Cannot change password for super admin', type: 'warning' });
            return;
        }
        setChangePasswordUser(user);
    };

    const handlePasswordSubmit = async (password: string) => {
        if (!changePasswordUser) return;
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/users/${changePasswordUser.id}/change-password`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    },
                    body: JSON.stringify({ new_password: password }),
                },
            );
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Failed to change password');
            }
            setToast({ message: 'Password changed successfully', type: 'success' });
            setChangePasswordUser(null);
        } catch (err: any) {
            setToast({ message: err.message || 'Failed to change password', type: 'error' });
            throw err;
        }
    };

    const handleAddNew = () => {
        if (!roles.length) {
            setToast({ message: 'No roles found. Please create a role before proceeding.', type: 'warning' });
            return;
        }
        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleModalClose = () => { setIsModalOpen(false); setEditingUser(null); };

    const handleModalSubmit = async (data: any) => {
        try {
            if (editingUser) {
                await userService.updateUser(editingUser.id, data);
                setToast({ message: 'User updated', type: 'success' });
            } else {
                await userService.createUser(data);
                setToast({ message: 'User created', type: 'success' });
            }
            setIsModalOpen(false);
            setEditingUser(null);
            await loadData();
        } catch (e) { console.error(e); }
    };

const handleClientModalSubmit = async (data: any) => {
    try {
        const client = await clientService.createClient(data); // ✅ RETURN THIS

        setToast({ message: 'Client created successfully', type: 'success' });
        setIsClientModalOpen(false);
        setCrossCreationData(null);

        return client; // 🔥 THIS IS THE FIX
    } catch (err: any) {
        setToast({ message: err?.message || 'Failed to create client', type: 'error' });
        throw err;
    }
};

    const handleUserTypeNext = (type: 'internal' | 'client') => {
        setUserTypeModalOpen(false);
        if (type === 'internal') { setIsModalOpen(true); return; }
        setClientSelectionOpen(true);
    };

    const handleClientSelected = (client: any) => {
        setSelectedClient(client);
        setClientSelectionOpen(false);
        setIsModalOpen(true);
    };

    const handleToggleStatus = (user: User) => {
        if (user.is_superuser) {
            setToast({ message: 'Cannot modify superuser status', type: 'warning' });
            return;
        }
        setConfirmModal({ isOpen: true, user, action: 'toggle' });
    };

    const handleConfirmAction = async () => {
        if (!confirmModal.user) return;
        try {
            if (confirmModal.user.statusCode === 'ACTIVE') {
                await userService.deactivateUser(confirmModal.user.id);
                setToast({ message: 'User deactivated successfully', type: 'success' });
            } else {
                await userService.activateUser(confirmModal.user.id);
                setToast({ message: 'User activated successfully', type: 'success' });
            }
            loadData();
        } catch (err: any) {
            setToast({ message: err?.message || 'Failed to perform action', type: 'error' });
        } finally {
            setConfirmModal({ isOpen: false, user: null, action: 'toggle' });
        }
    };

    const handleBulkUploadSuccess = (result: { created: number; failed: number }) => {
        setToast({
            message: `Bulk upload complete: ${result.created} created${result.failed > 0 ? `, ${result.failed} skipped` : ''}`,
            type: result.failed === 0 ? 'success' : 'warning',
        });
        setBulkUploadOpen(false);
        loadData();
    };

    // ─────────────────────────────────────────────────────────────────────────
    // STAT CARDS
    // ─────────────────────────────────────────────────────────────────────────
    const userStats = [
        {
            title: 'Total Users',
            value: stats?.total_users?.toString() ?? '0',
            icon: Users,
            color: 'blue',
            onClick: () => handleStatClick('total'),
            active: statusFilter.length === 0,
        },
        {
            title: 'Active Users',
            value: stats?.active_users?.toString() ?? '0',
            icon: UserCheck,
            color: 'green',
            onClick: () => handleStatClick('active'),
            active: statusFilter.includes('ACTIVE'),
        },
        {
            title: 'Inactive Users',
            value: stats?.inactive_users?.toString() ?? '0',
            icon: UserX,
            color: 'red',
            onClick: () => handleStatClick('inactive'),
            active: statusFilter.includes('INACTIVE'),
        },
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // TABLE COLUMNS
    // ─────────────────────────────────────────────────────────────────────────
    const userColumns = [
        {
            key: 'name',
            header: 'Name',
            render: (_: any, row: User) => `${row.first_name} ${row.last_name}`,
            width: '200px',
        },
        { key: 'email',    header: 'Email',           width: '220px' },
        { key: 'username', header: 'Alias / Username', width: '150px' },
        {
            key: 'phone',
            header: 'Phone',
            render: (_: any, row: User) => {
                const phone = (row as any).phone_country_code && (row as any).phone_number
                    ? `${(row as any).phone_country_code}-${(row as any).phone_number}`
                    : null;
                return phone
                    ? <span>{phone}</span>
                    : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>;
            },
            width: '140px',
        },
        // "Created By" – only for SUPER_ADMIN / ORG_ADMIN
        ...(isSuperAdmin || isOrgAdmin ? [{
            key: 'created_by_name',
            header: 'Created By',
            render: (_: any, row: User) =>
                row.created_by_name || (
                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                        {row.organisation_name == null ? 'Super Admin' : 'Organisation'}
                    </span>
                ),
            width: '180px',
        }] : []),
        // "Organisation" – only for SUPER_ADMIN
        ...(isSuperAdmin ? [{
            key: 'organisation_name',
            header: 'Organisation',
            render: (_: any, row: User) =>
                row.organisation_name || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>,
            width: '180px',
        }] : []),
        {
            key: 'roles',
            header: 'Roles',
            render: (roles: Array<{ id: string; name: string }>) => {
                if (!roles?.length) return 'No roles';
                if (roles.length === 1) return roles[0].name;
                return `${roles[0].name} +${roles.length - 1}`;
            },
            width: '160px',
        },
        {
            key: 'clients',
            header: 'Clients',
            render: (_: any, row: User) => {
                const count = row.client_count ?? 0;
                const text  = count === 0 ? 'No clients' : count === 1 ? '1 Client' : `${count} Clients`;
                const style = count > 0
                    ? { bg: '#e0f2fe', color: '#0369a1', hoverBg: '#bae6fd' }
                    : { bg: '#f3f4f6', color: '#6b7280', hoverBg: '#e5e7eb' };
                return (
                    <div
                        onClick={() => setClientMappingModal({ isOpen: true, user: row })}
                        style={{
                            display: 'inline-block', padding: '4px 12px',
                            background: style.bg, color: style.color,
                            borderRadius: '9999px', fontSize: '12px',
                            fontWeight: 500, cursor: 'pointer',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = style.hoverBg)}
                        onMouseLeave={e => (e.currentTarget.style.background = style.bg)}
                    >
                        {text}
                    </div>
                );
            },
            width: '150px',
        },
        {
            key: 'statusCode',
            header: 'Status',
            render: (value: string | null) => {
                const isActive = value === 'ACTIVE';
                return (
                    <span className={`${styles.statusBadge} ${isActive ? styles.active : styles.inactive}`}>
                        {isActive ? 'Active' : 'Inactive'}
                    </span>
                );
            },
            width: '150px',
        },
        // Actions – only for admins
        ...(canEditUser ? [{
            key: 'actions',
            header: 'Actions',
            render: (_: any, row: User) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Tooltip content={row.is_superuser ? 'Cannot edit superuser' : 'Edit'} preferredPosition="left">
                        <span style={{ display: 'inline-block' }}>
                            <button
                                className={`${styles.actionBtn} ${styles.edit}`}
                                onClick={() => !loadingEditId && handleEdit(row)}
                                disabled={!!loadingEditId || row.is_superuser}
                                style={{ opacity: (row.is_superuser || !!loadingEditId) ? 0.5 : 1, cursor: (row.is_superuser || !!loadingEditId) ? 'not-allowed' : 'pointer' }}
                            >
                                {loadingEditId === row.id ? <Loader2 size={14} className={styles.animateSpin} /> : <Edit2 size={14} />}
                            </button>
                        </span>
                    </Tooltip>
                    <Tooltip content={row.is_superuser ? 'Cannot change password' : 'Change Password'} preferredPosition="left">
                        <span style={{ display: 'inline-block' }}>
                            <button
                                className={`${styles.actionBtn} ${styles.key}`}
                                onClick={() => handleChangePassword(row)}
                                style={{ opacity: row.is_superuser ? 0.5 : 1, cursor: row.is_superuser ? 'not-allowed' : 'pointer' }}
                            >
                                <Key size={14} />
                            </button>
                        </span>
                    </Tooltip>
                    <Tooltip content={row.statusCode === 'ACTIVE' ? 'Deactivate' : 'Activate'} preferredPosition="left">
                        <span style={{ display: 'inline-block' }}>
                            <button
                                className={`${styles.actionBtn} ${row.statusCode === 'ACTIVE' ? styles.deactivate : styles.activate}`}
                                onClick={() => handleToggleStatus(row)}
                                style={{ opacity: row.is_superuser ? 0.5 : 1, cursor: row.is_superuser ? 'not-allowed' : 'pointer' }}
                            >
                                {row.statusCode === 'ACTIVE' ? <StopCircle size={14} /> : <PlayCircle size={14} />}
                            </button>
                        </span>
                    </Tooltip>
                </div>
            ),
            width: '130px',
        }] : []),
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    
if (!permissionsLoaded || isInitialLoading) {
    return <Loading message="Loading users..." />;
}    if (!canViewUser) {
    return (
        <div className={styles.managementContent}>
            <div style={{ textAlign: 'center', padding: '80px', color: '#64748b' }}>
                <h2>No Access</h2>
                <p>You do not have permission to view users.</p>
            </div>
        </div>
    );
}
    return (
        <div className={styles.managementContent}>

            {/* ── Stat Cards ── */}
            <div className={styles.statsGrid}>
                {userStats.map((stat, i) => (
                    <div
                        key={i}
                        className={`${styles.statCard} ${styles[stat.color]} ${styles.clickable} ${stat.active ? styles.selected : ''}`}
                        onClick={stat.onClick}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className={styles.statIcon}><stat.icon size={16} /></div>
                        <div className={styles.statContent}>
                            <h3>{stat.value}</h3>
                            <p>{stat.title}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Table Section ── */}
            <div className={styles.tableSection} style={{ position: 'relative' }}>
                <div className={styles.tableHeader}>

                    {/* ── Internal / Client tabs (SUPER_ADMIN + ORG_ADMIN only) ── */}
                    {canSeeTabs ? (
                        <div className={styles.headerTabs}>
                            <button
                                onClick={() => { setActiveTab('internal'); setCurrentPage(0); }}
                                className={`${styles.headerTabBtn} ${activeTab === 'internal' ? styles.active : ''}`}
                            >
                                <Shield size={16} />
                                Internal Users
                            </button>
                            <button
                                onClick={() => { setActiveTab('client'); setCurrentPage(0); }}
                                className={`${styles.headerTabBtn} ${activeTab === 'client' ? styles.active : ''}`}
                            >
                                <Building2 size={16} />
                                Client Users
                            </button>
                        </div>
                    ) : (
                        <div className={styles.sectionTitle}>
                            <Users size={18} />
                            <span>Users</span>
                        </div>
                    )}

                    {/* ── Controls row ── */}
                    {/* ── Controls Row (Search + Buttons) ── */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
                        {/* Search */}
                        <div style={{ position: 'relative', width: '280px' }}>
                            <Search size={16} style={{
                                position: 'absolute', left: '12px', top: '50%',
                                transform: 'translateY(-50%)', color: '#9ca3af',
                                pointerEvents: 'none', zIndex: 1
                            }} />

                            <input
                                type="text"
                                placeholder="Search by name, email..."
                                className={styles.filterInput}
                                value={searchQuery}
                                onChange={e => handleSearchChange(e.target.value)}
                            />
                        </div>

                        {/* Right Actions */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {canUseFilters && (
                                <button
                                    className={`${styles.filterButton} ${activeFilterCount > 0 ? styles.active : ''}`}
                                    onClick={handleOpenFilters}
                                >
                                    <Filter size={14} /> Filters
                                    {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
                                </button>
                            )}

                            {canCreateUser && (
                                <>
                                    <button className={styles.addBtn} onClick={handleAddNew}>
                                        <UserPlus size={14} /> Add User
                                    </button>

                                    <button className={styles.addBtn} onClick={() => setBulkUploadOpen(true)}>
                                        <Upload size={14} /> Import Users
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <Table
                    columns={userColumns}
                    data={users}
                    maxHeight="calc(100vh - 360px)"
                    className={styles.userTableContainer}
                />

                {loading && !isInitialLoading && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                        <Loading message="Updating..." />
                    </div>
                )}
            </div>

            <CommonPagination
                show={totalUsers > 0}
                pageCount={Math.ceil(totalUsers / itemsPerPage)}
                currentPage={currentPage}
                totalItems={totalUsers}
                itemsPerPage={itemsPerPage}
                onPageChange={(data: any) => setCurrentPage(data.selected)}
                onItemsPerPageChange={(items: number) => { setItemsPerPage(items); setCurrentPage(0); }}
            />

            {/* ── Modals ── */}
            <UserTypeModal isOpen={userTypeModalOpen} onClose={() => setUserTypeModalOpen(false)} onNext={handleUserTypeNext} />

            <ClientSelectionModal isOpen={clientSelectionOpen} onClose={() => setClientSelectionOpen(false)} onSelect={handleClientSelected} />

            <UserModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
                title={editingUser ? 'Edit User' : 'Add User'}
                initialData={
                    editingUser
                        ? {
                            id:           editingUser.id,
                            email:        editingUser.email        || '',
                            username:     editingUser.username     || '',
                            first_name:   editingUser.first_name   || '',
                            middle_name:  editingUser.middle_name  || '',
                            last_name:    editingUser.last_name    || '',
                            emp_id:       editingUser.emp_id       || '',
                            date_of_birth: editingUser.date_of_birth || '',
                            phone_country_code: (editingUser as any).phone_country_code || '',
                            phone_number:       (editingUser as any).phone_number       || '',
                            roles:        editingUser.roles        || [],
                            supervisor_id:editingUser.supervisor_id || undefined,
                            client_id:    editingUser.client_id,
                            client_name:  (editingUser as any).client_name,
                          }
                        : undefined
                }
                roles={roles}
                clientAdminRoleId={roles.find(r => r.name === 'CLIENT_ADMIN')?.id}
                allowUserTypeSelection={isSuperAdmin || isOrgAdmin}
            />

            <ChangePasswordModal
                isOpen={!!changePasswordUser}
                onClose={() => setChangePasswordUser(null)}
                onSubmit={handlePasswordSubmit}
                username={changePasswordUser?.username || ''}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, user: null, action: 'toggle' })}
                onConfirm={handleConfirmAction}
                title={confirmModal.user?.statusCode === 'ACTIVE' ? 'Deactivate User' : 'Activate User'}
                message={`Are you sure you want to ${confirmModal.user?.statusCode === 'ACTIVE' ? 'deactivate' : 'activate'} this user?`}
                confirmText={confirmModal.user?.statusCode === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                type="warning"
            />

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            <ConfirmModal
                isOpen={showCrossCreationConfirm}
                onClose={() => setShowCrossCreationConfirm(false)}
                onConfirm={() => { setShowCrossCreationConfirm(false); setIsClientModalOpen(true); }}
                title="Create Linked Client"
                message="User created successfully. Do you want to create a linked Client entity for this user?"
                confirmText="Yes, Create Client"
                type="info"
            />

            <ClientModal
                isOpen={isClientModalOpen}
                onClose={() => setIsClientModalOpen(false)}
                onSubmit={handleClientModalSubmit}
                initialData={crossCreationData}
                title="Create Linked Client"
            />

            <ClientMappingModal
                isOpen={clientMappingModal.isOpen}
                onClose={() => setClientMappingModal({ isOpen: false, user: null })}
                user={clientMappingModal.user}
                onUpdate={loadData}
            />

            <BulkUserUploadModal
                isOpen={bulkUploadOpen}
                onClose={() => setBulkUploadOpen(false)}
                onSuccess={handleBulkUploadSuccess}
                roles={roles}
                currentUser={currentUser}
            />

            {/* ── Filter Offcanvas ── */}
            <div className={`${styles.offcanvas} ${showFilters ? styles.offcanvasOpen : ''}`}>
                <div className={styles.offcanvasOverlay} onClick={() => setShowFilters(false)} />
                <div className={styles.offcanvasContent}>
                    <div className={styles.offcanvasHeader}>
                        <h3>Filters</h3>
                        <button className={styles.closeButton} onClick={() => setShowFilters(false)}>
                            <X size={20} />
                        </button>
                    </div>
                    <div className={styles.offcanvasBody}>

                        {/* Organisation – SUPER_ADMIN only */}
                        {canFilterOrg && (
                            <div className={styles.filterGroupItem}>
                                <label>Organisation</label>
                                <CommonMultiSelect
                                    options={organisations}
                                    value={tempSelectedOrg}
                                    onChange={(val: string[]) => {
                                        setTempSelectedOrg(val);
                                        // If org changes, reset client filter to only valid clients
                                        if (val.length > 0) {
                                            const selectedOrgLabels = organisations.filter(o => val.includes(o.value)).map(o => o.label);
                                            setTempSelectedClientFilter(prev =>
                                                prev.filter(id => {
                                                    const client = allClients.find(c => c.value === id);
                                                    return client?.organisationName && selectedOrgLabels.some(name => client.organisationName?.includes(name));
                                                })
                                            );
                                        }
                                    }}
                                    placeholder="Filter by Organisation"
                                    size="md"
                                />
                            </div>
                        )}

                        {/* Client – SUPER_ADMIN + ORG_ADMIN */}
                        {canFilterClient && (
                            <div className={styles.filterGroupItem}>
                                <label>Client</label>
                                <CommonMultiSelect
                                    options={allClients.filter(c => {
                                        if (tempSelectedOrg.length === 0) return true;
                                        const selectedOrgLabels = organisations.filter(o => tempSelectedOrg.includes(o.value)).map(o => o.label);
                                        return c.organisationName && selectedOrgLabels.some(name => c.organisationName?.includes(name));
                                    })}
                                    value={tempSelectedClientFilter}
                                    onChange={(val: string[]) => setTempSelectedClientFilter(val)}
                                    placeholder="Filter by Client"
                                    size="md"
                                />
                            </div>
                        )}

                        {/* Role */}
                        <div className={styles.filterGroupItem}>
                            <label>Role</label>
                            <CommonMultiSelect
                                options={roleOptions}
                                value={tempSelectedRole}
                                onChange={(val: string[]) => setTempSelectedRole(val)}
                                placeholder="Filter by Role"
                                size="md"
                                isSearchable
                                onInputChange={(val: string) => setRoleSearch(val)}
                            />
                        </div>

                        {/* Created By */}
                        <div className={styles.filterGroupItem}>
                            <label>Created By</label>
                            <CommonMultiSelect
                                options={createdByOptions}
                                value={tempCreatedByFilter}
                                onChange={(val: string[]) => setTempCreatedByFilter(val)}
                                placeholder="Filter by Creator"
                                size="md"
                                isSearchable
                                onInputChange={(val: string) => setCreatorSearch(val)}
                            />
                        </div>

                        {/* Status */}
                        <div className={styles.filterGroupItem}>
                            <label>Status</label>
                            <CommonMultiSelect
                                options={[
                                    { label: 'Active',   value: 'ACTIVE' },
                                    { label: 'Inactive', value: 'INACTIVE' },
                                ]}
                                value={tempStatusFilter}
                                onChange={(val: string[]) => setTempStatusFilter(val)}
                                placeholder="Filter by Status"
                                size="md"
                            />
                        </div>
                    </div>
                    <div className={styles.offcanvasFooter}>
                        <button className={styles.resetButton} onClick={handleResetFilters}>Reset</button>
                        <button className={styles.applyButton} onClick={handleApplyFilters}>Apply Filters</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;